import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  Box,
  Avatar,
} from "@mui/material";
import ApplicantsToolbar from "./ApplicantsToolbar";
import { useDispatch, useSelector } from "react-redux";
import { getService } from "../../reducers/service";
import {
  deleteApplicant,
  getApplicants,
} from "../../reducers/ApplicantsReducer";
import PATEditButton from "../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { paginationStyle, serviceNames } from "../../utils/constants";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import {
  formatDateToMonthDateYear,
  formatPhoneNumber,
  formatSSN,
  mapDictionaryOptions,
  formatToBase64ImageUrl,
  getInitials,
  getRowsPerPageOptions,
} from "../../utils/functions";
import {
  PATH_APPLICANTS_ADD,
  PATH_APPLICANTS_EDIT,
} from "../../utils/routeConstants";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import PATViewButton from "../../components/Buttons/PATViewButton";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

const initialFilterState = {
  ssn: "",
  lastName: "",
  firstName: "",
  middleName: "",
  status: [],
  tagTypes: [],
  yearApplied: "",
};

const Applicants = () => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { applicants, loading, total } = useSelector(
    (state) => state.applicants
  );
  const { userSettings } = useSelector((state) => state.userSettings);
  const themeMode = useSelector((state) => state.theme.mode);
  const { userData, isAuthenticated } = useSelector((state) => state.user);
  const userAgency = useSelector((state) => state.user.userAgency);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
  const [selectedId, setSelectedId] = useState(null);
  const [columnsOrder, setColumnsOrder] = useState([]);
  const [columnsSize, setColumnsSize] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filters, setFilters] = useState(initialFilterState);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [yearAppliedOptions, setYearAppliedOptions] = useState([]);

  const statusOptions = mapDictionaryOptions(dictionary.codePerApplicantStatus);
  const applicantTestResultOptions = mapDictionaryOptions(
    dictionary.codePerApplicantTestResult
  );
  const raceOptions = mapDictionaryOptions(dictionary.codeRace);
  const genderOptions = mapDictionaryOptions(dictionary.codeSex);
  const ethnicityOptions = mapDictionaryOptions(dictionary.codeEthnicity);
  const contactInfoOptions = mapDictionaryOptions(
    dictionary.codeContactInfoMethodTypes
  );
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);
  const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);

  const applicantPermission = getAccessPermission(
    "applicants",
    "Applicant Record",
    "any"
  );
  const canReadApplicants = applicantPermission?.Read ?? true;
  const canEditApplicants = applicantPermission?.Edit ?? true;
  const canDeleteApplicants = applicantPermission?.Delete ?? true;

  const fetchDropdownValues = async () => {
    const services = {
      yearOptions: getService(serviceNames.perApplicantYearOptions),
    };

    const results = await Promise.all([
      services.yearOptions.find({
        query: {
          agencyId: userAgency,
        },
      }),
    ]);

    if (Array.isArray(results[0])) {
      setYearAppliedOptions(
        results[0].map((value) => ({
          label: value,
          value,
        }))
      );
    }
  };

  useEffect(() => {
    if (!settingsLoaded || !isAuthenticated) return;
    const { field, sort } = sortModel[0] || {};
    if (field && sort) {
      dispatch(getApplicants(page, pageSize, field, sort, filters, userAgency));
    }
  }, [
    dispatch,
    page,
    pageSize,
    sortModel,
    filters,
    settingsLoaded,
    isAuthenticated,
    userAgency,
  ]);

  const baseColumns = useMemo(
    () => [
      {
        field: "thumbnail",
        headerName: "Thumbnail",
        width: 80,
        sortable: false,
        renderCell: ({ row }) => {
          let imageUrl = "";

          if (row.thumbnail?.imageData?.byteLength) {
            const base64 = formatToBase64ImageUrl(row.thumbnail.imageData);
            if (base64) imageUrl = base64;
          }

          return imageUrl ? (
            <img
              src={imageUrl}
              alt="Thumbnail"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                bgcolor: "#90caf9",
              }}
            >
              {getInitials(row.firstName, row.lastName)}
            </Avatar>
          );
        },
      },
      {
        field: "firstName",
        headerName: "First Name",
        width: 150,
        sortable: true,
      },
      {
        field: "lastName",
        headerName: "Last Name",
        width: 150,
        sortable: true,
      },
      {
        field: "middleName",
        headerName: "Middle Name",
        width: 150,
        sortable: true,
      },
      {
        field: "dob",
        headerName: "DOB",
        width: 150,
        sortable: true,
        valueFormatter: (params) => {
          return formatDateToMonthDateYear(params.value);
        },
      },
      {
        field: "yearApplied",
        headerName: "Year Applied",
        width: 150,
        sortable: true,
      },
      {
        field: "phone",
        headerName: "Phone",
        width: 150,
        sortable: true,
        valueFormatter: (params) => {
          return params.value ? formatPhoneNumber(params.value) : "";
        },
      },
      {
        field: "ssn",
        headerName: "SSN",
        width: 100,
        sortable: true,
        valueFormatter: (params) => {
          return params.value ? formatSSN(params.value) : "";
        },
      },
      {
        field: "ethnicityInfo.description",
        headerName: "Ethnicity",
        width: 100,
      },
      {
        field: "genderInfo.description",
        headerName: "Gender",
        width: 100,
      },
      {
        field: "applicantStatusInfo.description",
        headerName: "Status",
        width: 100,
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 120,
        align: "center",
        sortable: false,
        renderCell: ({ row }) =>
          row.id ? (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canEditApplicants && (
                      <PATEditButton
                        tooltipText="Edit Applicant"
                        onClick={() =>
                          history.push(`${PATH_APPLICANTS_EDIT}${row.id}`)
                        }
                        size="20px"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canReadApplicants && !canEditApplicants && (
                      <PATViewButton
                        tooltipText="View Applicant Details"
                        onClick={() =>
                          history.push({
                            pathname: `${PATH_APPLICANTS_EDIT}${row.id}`,
                            search: "?readOnly=true",
                          })
                        }
                        size="20px"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canDeleteApplicants && (
                      <PATDeleteButton
                        tooltipText="Delete Applicant"
                        onClick={() => {
                          setSelectedId(row.id);
                          setOpenDeleteDialog(true);
                        }}
                        size="20px"
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : null,
      },
    ],
    [history, canEditApplicants, canDeleteApplicants, canReadApplicants]
  );

  const columns = useMemo(() => {
    const hasOrder = columnsOrder.length > 0;
    const hasSizes = Object.keys(columnsSize).length > 0;

    if (!hasOrder && !hasSizes) return baseColumns;

    let baseOrderedColumns;

    if (hasOrder) {
      const ordered = [
        "thumbnail",
        ...columnsOrder.filter((field) => field !== "thumbnail"),
      ]
        .map((field) => baseColumns.find((col) => col.field === field))
        .filter(Boolean);

      const remaining = baseColumns.filter(
        (col) => !ordered.some((o) => o.field === col.field)
      );

      const actionsIndex = ordered.findIndex((col) => col.field === "actions");
      const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

      baseOrderedColumns = [
        ...ordered.slice(0, insertAt),
        ...remaining,
        ...ordered.slice(insertAt),
      ];
    } else {
      baseOrderedColumns = baseColumns;
    }

    const finalColumns = baseOrderedColumns.map((col) => ({
      ...col,
      width: columnsSize[col.field] ?? col.width ?? 100,
    }));

    return finalColumns;
  }, [baseColumns, columnsOrder, columnsSize]);

  const allColumnFields = baseColumns.map((col) => col.field);
  useEffect(() => {
    const defaultVisibility = {};
    allColumnFields.forEach((field) => {
      defaultVisibility[field] = true;
    });
    setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
  }, []);

  useEffect(() => {
    if (!isAuthenticated || settingsLoaded) return;

    const initialize = async () => {
      await fetchDropdownValues();
      const settingsMap = Object.fromEntries(
        userSettings.map((s) => [s.key, s.value])
      );

      if (settingsMap["applicantsTableColumnOrder"]) {
        setColumnsOrder(JSON.parse(settingsMap["applicantsTableColumnOrder"]));
      }

      if (settingsMap["applicantsTableColumnSizes"]) {
        const widthArray = JSON.parse(
          settingsMap["applicantsTableColumnSizes"]
        );
        const result = widthArray.reduce((acc, curr) => {
          acc[curr.field] = curr.width;
          return acc;
        }, {});
        setColumnsSize(result);
      }

      if (settingsMap["applicantsTableColumnVisibility"]) {
        setColumnVisibilityModel(
          JSON.parse(settingsMap["applicantsTableColumnVisibility"])
        );
      }

      if (settingsMap["applicantsTableSortModel"]) {
        const savedSortModel = JSON.parse(
          settingsMap["applicantsTableSortModel"]
        );
        if (Array.isArray(savedSortModel)) {
          const validSortModel = savedSortModel.filter((model) =>
            baseColumns.some((col) => col.field === model.field)
          );
          if (validSortModel.length > 0) {
            setSortModel(savedSortModel);
          }
        }
      }

      setSettingsLoaded(true);
    };

    initialize();
  }, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

  useEffect(() => {
    if (isAuthenticated && userAgency) {
      fetchDropdownValues();
    }
  }, [userAgency, isAuthenticated]);

  const handleSortModelChange = useCallback(
    (model) => {
      const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;
      // Avoid unnecessary state updates if sort model didn't change
      const isSortModelDifferent =
        JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

      if (isSortModelDifferent) {
        setSortModel(newSortModel);
        setPage(INITIAL_PAGE);

        if (userData?.user?.ptsUserID) {
          dispatch(
            updateOrCreateUserSetting(
              userData.user.ptsUserID,
              "applicantsTableSortModel",
              JSON.stringify(newSortModel)
            )
          );
        }
      }
    },
    [sortModel, dispatch, userData?.user?.ptsUserID]
  );

  const handleColumnOrderChange = useCallback(
    (params, event, details) => {
      const newColumnOrder = details.api
        .getAllColumns()
        .map((col) => col.field);
      if (userData?.user?.ptsUserID) {
        dispatch(
          updateOrCreateUserSetting(
            userData.user.ptsUserID,
            "applicantsTableColumnOrder",
            JSON.stringify(newColumnOrder)
          )
        );
      }
    },
    [dispatch, userData?.user?.ptsUserID]
  );

  const handleColumnWidthChange = (params, event, details) => {
    const columnSizes = details.api.getAllColumns().map((col) => {
      return {
        field: col.field,
        width: col.width,
      };
    });
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          "applicantsTableColumnSizes",
          JSON.stringify(columnSizes)
        )
      );
    }
  };

  const handleColumnVisibilityModelChange = useCallback(
    (newModel) => {
      setColumnVisibilityModel(newModel);
      if (userData?.user?.ptsUserID) {
        dispatch(
          updateOrCreateUserSetting(
            userData.user.ptsUserID,
            "applicantsTableColumnVisibility",
            JSON.stringify(newModel)
          )
        );
      }
    },
    [dispatch, userData?.user?.ptsUserID]
  );

  const handleDelete = useCallback(() => {
    if (selectedId) {
      dispatch(deleteApplicant(selectedId, userData?.user?.ptsUserID));
      setOpenDeleteDialog(false);
      setSelectedId(null);
    }
  }, [dispatch, selectedId, userData?.user?.ptsUserID]);

  const resetFilters = () => {
    setFilters(initialFilterState);
  };

  const rowsPerPageOptions = useMemo(
    () => getRowsPerPageOptions(total),
    [total]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 85px)", // Adjust based on header height
        marginTop: theme.spacing(0.5),
      }}
    >
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          m: 1,
          p: 2,
        }}
      >
        {canReadApplicants ? (
          <>
            <ApplicantsToolbar
              onAddClick={() => history.push(`${PATH_APPLICANTS_ADD}`)}
              applicants={applicants}
              onFilterChange={setFilters}
              filters={filters}
              statusOptions={statusOptions}
              yearAppliedOptions={yearAppliedOptions}
              applicantTestResultOptions={applicantTestResultOptions}
              raceOptions={raceOptions}
              genderOptions={genderOptions}
              ethnicityOptions={ethnicityOptions}
              contactInfoOptions={contactInfoOptions}
              tagTypeOptions={tagTypeOptions}
              suffixOptions={suffixOptions}
              resetFilters={resetFilters}
            />

            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DataGridPro
                rows={applicants}
                columns={columns}
                initialState={{ pinnedColumns: { right: ["actions"] } }}
                getRowId={(row) => row.GUID}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={
                  handleColumnVisibilityModelChange
                }
                pageSize={pageSize}
                rowsPerPageOptions={rowsPerPageOptions}
                pagination
                paginationMode="server"
                sortModel={sortModel}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                rowCount={total}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={loading}
                disableSelectionOnClick
                onColumnOrderChange={handleColumnOrderChange}
                onColumnWidthChange={handleColumnWidthChange}
                sx={{
                  ...paginationStyle(theme, themeMode),
                  color: theme.palette.colorFont,
                  margin: 1,
                }}
              />
            </Box>

            <DeleteConfirmationDialog
              open={openDeleteDialog}
              onClose={() => {
                setOpenDeleteDialog(false);
                setSelectedId(null);
              }}
              onConfirm={handleDelete}
              title="Delete Applicant"
              message="Are you sure you want to delete this applicant?"
            />
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <h3>You don't have permission to view applicants.</h3>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Applicants;
